import { useCallback, useMemo } from "react";
import { useIntl } from "react-intl";
import { Form } from "react-bootstrap";
import styled from "styled-components";
import { FieldProps } from "react-jsonschema-form";
import { ActionMeta, ValueType } from "react-select";
import { arrayMove, SortEndHandler } from "react-sortable-hoc";
import { useProperty } from "../../components/property-provider";
import { SelectOption, SortableSelect } from "../shared/widgets/SortableSelect";
import { useExtrasQuery } from "../shared/widgets/modificationPriceManagement/extraQueries";
import { messages } from "./messages";

const Container = styled.div`
  margin-bottom: 1rem;
  margin-top: 1rem;
`;

const HelpText = styled.small`
  color: #6c757d;
  display: block;
  margin-top: 0.25rem;
`;

const Label = styled(Form.Label)``;

function toOptions(tickers: string[], allExtras: SelectOption[]): SelectOption[] {
  return tickers
    .filter(t => t)
    .map(ticker => allExtras.find(o => o.value === ticker) ?? { value: ticker, label: ticker });
}

export function HighlightReelOrderWidget({ formData, onChange }: FieldProps) {
  const intl = useIntl();
  const property = useProperty();
  const { data: extrasList } = useExtrasQuery(property?.ticker ?? "");

  const allExtras: SelectOption[] = useMemo(
    () => (extrasList ?? []).map(e => ({ value: e.ticker, label: e.ticker })),
    [extrasList]
  );

  const currentValue = (formData as string | undefined) ?? "";
  const selectedOptions = toOptions(currentValue.split(",").filter(Boolean), allExtras);

  const handleChange = useCallback(
    (opts: ValueType<SelectOption, true>, _: ActionMeta<SelectOption>) => {
      onChange(opts && opts.length > 0 ? [...opts].map(o => o.value).join(",") : "");
    },
    [onChange]
  );

  const handleSortEnd: SortEndHandler = useCallback(
    ({ oldIndex, newIndex }) => {
      const reordered = arrayMove(selectedOptions, oldIndex, newIndex);
      onChange(reordered.map(o => o.value).join(","));
    },
    [selectedOptions, onChange]
  );

  return (
    <Container>
      <Form.Group>
        <Label>{intl.formatMessage(messages.engineHighlightReelOrder)}</Label>
        <SortableSelect
          value={selectedOptions}
          options={allExtras}
          onChange={handleChange}
          onSortEnd={handleSortEnd}
          placeholder=""
        />
        <HelpText>{intl.formatMessage(messages.highlightReelHelpText)}</HelpText>
      </Form.Group>
    </Container>
  );
}
